import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    ArrowLeft,
    Box,
    UserCircle,
    Building2,
    Calendar,
    Save,
    Signature,
    Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from '../../components/ui/dialog';
import { Combobox } from "../../components/ui/combobox";
import { MultiSelect } from "../../components/ui/multi-select";
import { Input } from '../../components/ui/input';
import api from '../../utils/api';
import { Asset } from '../../types/asset';
import { User, Department } from '../../types';
import { toast } from 'sonner';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';

const AssignAssets: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const location = useLocation();

    // Check if we navigated here with a pre-selected asset
    const preSelectedAsset = location.state?.preSelectedAsset;

    const [assets, setAssets] = useState<Asset[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Form State
    const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
    const [assignTargetType, setAssignTargetType] = useState<'user' | 'department'>('user');
    const [selectedUser, setSelectedUser] = useState<string>('');
    const [selectedDepartment, setSelectedDepartment] = useState<string>('');
    const [assignmentDate, setAssignmentDate] = useState(new Date().toISOString().split('T')[0]);
    const [notes, setNotes] = useState('');

    // Signature State
    const [signatureType, setSignatureType] = useState<'Draw' | 'Type'>('Type');
    const [signatureData, setSignatureData] = useState('');
    const [typedSignature, setTypedSignature] = useState('');
    const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [assetsRes, usersRes, deptsRes] = await Promise.all([
                    api.get<Asset[]>('/api/assets'),
                    api.get<{ users: User[] }>('/api/users?archived=false&all_users=true').catch(() => ({ users: [] })),
                    api.get<{ departments: Department[] }>('/api/departments').catch(() => ({ departments: [] }))
                ]);

                // Allow assignment of any asset that is 'Available' or 'In Triage'
                const assignableAssets = assetsRes.filter(a =>
                    !a.assignedToUser &&
                    !a.assignedToDepartment &&
                    (a.status === 'Available' || a.status === 'In Triage')
                );

                setAssets(assignableAssets);
                setUsers(usersRes.users || []);
                setDepartments(deptsRes.departments || []);

                if (preSelectedAsset) {
                    if (assignableAssets.some(a => a.id.toString() === preSelectedAsset)) {
                        setSelectedAssets([preSelectedAsset]);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load required data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (!ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_ASSIGNMENT)) {
        return <AccessRestricted />;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (selectedAssets.length === 0) {
            toast.error('Please select at least one asset');
            return;
        }

        if (assignTargetType === 'user' && !selectedUser) {
            toast.error('Please select a user');
            return;
        }

        if (assignTargetType === 'department' && !selectedDepartment) {
            toast.error('Please select a department');
            return;
        }

        if (!assignmentDate) {
            toast.error('Please enter assignment date');
            return;
        }

        if (!signatureData) {
            toast.error('Please provide a digital signature');
            return;
        }

        try {
            setSubmitting(true);

            const payload = {
                assignedToUserId: assignTargetType === 'user' ? parseInt(selectedUser) : null,
                assignedToDepartmentId: assignTargetType === 'department' ? parseInt(selectedDepartment) : null,
                notes,
                assignedDate: assignmentDate,
                signatureType,
                signatureData
            };

            // Process all selected assets in parallel
            await Promise.all(selectedAssets.map(assetId =>
                api.post(`/api/assets/${assetId}/assign`, payload)
            ));

            toast.success(`Successfully assigned ${selectedAssets.length} asset(s)`);
            navigate('/assets');
        } catch (error) {
            console.error('Error assigning assets:', error);
            toast.error('Failed to assign assets');
        } finally {
            setSubmitting(false);
        }
    };

    const assetOptions = assets.map(a => ({
        value: a.id.toString(),
        label: `${a.name} (${a.tag})`,
        category: a.type
    }));

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
        setIsDrawing(true);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const rect = canvas.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const rect = canvas.getBoundingClientRect();
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.stroke();
    };

    const stopDrawing = () => setIsDrawing(false);

    const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const touch = e.touches[0];
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        setIsDrawing(true);
        ctx.beginPath();
        ctx.moveTo(
            (touch.clientX - rect.left) * scaleX,
            (touch.clientY - rect.top) * scaleY
        );
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const touch = e.touches[0];
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        ctx.lineTo(
            (touch.clientX - rect.left) * scaleX,
            (touch.clientY - rect.top) * scaleY
        );
        ctx.stroke();
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const handleConfirmSignature = () => {
        if (signatureType === 'Type') {
            if (!typedSignature.trim()) {
                toast.error('Please enter your name');
                return;
            }
            setSignatureData(typedSignature.trim());
        } else {
            if (canvasRef.current) {
                const dataUrl = canvasRef.current.toDataURL();
                setSignatureData(dataUrl);
            }
        }
        setIsSignatureDialogOpen(false);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-6 mb-8">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50 flex-shrink-0"
                    onClick={() => navigate(-1)}
                >
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Assign Assets</h1>
                    <p className="text-gray-500 text-sm font-medium mt-1">
                        Select one or more available or in triage assets and assign them to a user or department
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col xl:flex-row gap-6">
                {/* Main Content Area */}
                <div className="flex-1 space-y-6">
                    {/* Asset Selection block */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <Box className="text-gray-400" size={20} />
                            <div>
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Select Assets</h2>
                                <p className="text-xs font-medium text-gray-400 mt-0.5">Only available or in triage assets can be selected</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <label className="text-xs font-bold text-gray-900">Assets to Assign <span className="text-red-500">*</span></label>
                            <MultiSelect
                                options={assetOptions}
                                selected={selectedAssets}
                                onChange={setSelectedAssets}
                                placeholder="Select assets..."
                                className="bg-gray-50/50"
                            />
                        </div>
                    </div>

                    {/* Assignment Details */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <UserCircle className="text-gray-400" size={20} />
                            <div>
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Assignment Record</h2>
                                <p className="text-xs font-medium text-gray-400 mt-0.5">Who receives these assets?</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-y-6">
                            <div className="space-y-3">
                                <label className="text-xs font-bold text-gray-900">Assign To <span className="text-red-500">*</span></label>
                                <div className="flex flex-col sm:flex-row gap-4 p-1 bg-gray-50/50 rounded-2xl border border-gray-100 w-fit">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setAssignTargetType('user');
                                            setSelectedDepartment('');
                                        }}
                                        className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${assignTargetType === 'user' ? 'bg-white text-blue-600 shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        <UserCircle size={16} /> User
                                    </button>
                                    {departments.filter(d => !d.archived).length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setAssignTargetType('department');
                                                setSelectedUser('');
                                            }}
                                            className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${assignTargetType === 'department' ? 'bg-white text-blue-600 shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700'}`}
                                        >
                                            <Building2 size={16} /> Department
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {assignTargetType === 'user' ? (
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-900">Select User <span className="text-red-500">*</span></label>
                                        <Combobox
                                            options={users.map(user => ({
                                                value: user.id.toString(),
                                                label: user.name,
                                                subtitle: user.jobRole || 'No role'
                                            }))}
                                            value={selectedUser}
                                            onChange={(val) => val && setSelectedUser(val)}
                                            placeholder="Select user..."
                                            searchPlaceholder="Search user..."
                                            emptyMessage="No user found."
                                            className="rounded-2xl border-gray-100 bg-gray-50/50 h-12"
                                        />
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-900">Select Department <span className="text-red-500">*</span></label>
                                        <Combobox
                                            options={departments.filter(d => !d.archived).map(dept => ({
                                                value: dept.id.toString(),
                                                label: dept.departmentName,
                                                subtitle: dept.departmentCode
                                            }))}
                                            value={selectedDepartment}
                                            onChange={(val) => val && setSelectedDepartment(val)}
                                            placeholder="Select department..."
                                            searchPlaceholder="Search department..."
                                            emptyMessage="No department found."
                                            className="rounded-2xl border-gray-100 bg-gray-50/50 h-12"
                                        />
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-900">Assignment Date <span className="text-red-500">*</span></label>
                                    <Input
                                        type="date"
                                        value={assignmentDate}
                                        onChange={(e) => setAssignmentDate(e.target.value)}
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Assignment Notes</label>
                                <Textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Enter any notes or conditions for this assignment..."
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 min-h-[100px] p-4 resize-none"
                                />
                            </div>

                            {/* Signature Section */}
                            <div className="space-y-3 pt-4 border-t border-gray-50">
                                <label className="text-xs font-bold text-gray-900">Digital Signature <span className="text-red-500">*</span></label>
                                {signatureData ? (
                                    <div className="relative group p-4 rounded-2xl bg-gray-50/50 border border-gray-100 border-dashed flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center text-green-600">
                                                <Check size={14} />
                                            </div>
                                            {signatureType === 'Draw' ? (
                                                <img src={signatureData} alt="Signature" className="max-h-10" />
                                            ) : (
                                                <span className="font-signature text-xl text-gray-800">{signatureData}</span>
                                            )}
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl px-3"
                                            onClick={() => setSignatureData('')}
                                        >
                                            Change
                                        </Button>
                                    </div>
                                ) : (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full rounded-2xl border-dashed border-gray-200 h-20 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-blue-500 hover:border-blue-500 hover:bg-blue-50/30 transition-all"
                                        onClick={() => setIsSignatureDialogOpen(true)}
                                    >
                                        <Signature size={24} />
                                        <span className="text-xs font-bold uppercase tracking-widest">Capture Signature</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar Sticky Area */}
                <div className="w-full xl:w-[350px] flex-shrink-0">
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm sticky top-6">
                        <div className="flex items-center gap-3 mb-8">
                            <Box className="text-gray-400" size={20} />
                            <h2 className="text-base font-bold text-gray-900 font-poppins">Summary</h2>
                        </div>

                        <div className="space-y-6 mb-8">
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Selected Assets</span>
                                <p className="text-sm font-medium text-gray-900">{selectedAssets.length} asset(s) chosen</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Assigning To</span>
                                <p className="text-sm font-medium text-gray-900">
                                    {assignTargetType === 'user'
                                        ? (selectedUser ? users.find(u => u.id.toString() === selectedUser)?.name : 'Not selected')
                                        : (selectedDepartment ? departments.find(d => d.id.toString() === selectedDepartment)?.departmentName : 'Not selected')}
                                </p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Date</span>
                                <p className="text-sm font-medium text-gray-900">{assignmentDate || 'Not specified'}</p>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full rounded-2xl bg-slate-900 hover:bg-slate-800 text-white h-12 font-bold mb-6 gap-2 text-sm shadow-md transition-all"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <>
                                    <Save size={18} />
                                    Confirm Assignment
                                </>
                            )}
                        </Button>

                        <div className="space-y-3">
                            <div className="flex items-start gap-2 text-xs font-medium text-gray-500">
                                <span className="text-gray-300 mt-0.5">•</span>
                                <span>Assets will be marked as Assigned</span>
                            </div>
                            <div className="flex items-start gap-2 text-xs font-medium text-gray-500">
                                <span className="text-gray-300 mt-0.5">•</span>
                                <span>Assignment history will be updated</span>
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            <Dialog open={isSignatureDialogOpen} onOpenChange={setIsSignatureDialogOpen}>
                <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Worker Signature</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant={signatureType === 'Type' ? 'default' : 'outline'}
                                onClick={() => setSignatureType('Type')}
                                className="flex-1 rounded-xl"
                            >
                                Type
                            </Button>
                            <Button
                                type="button"
                                variant={signatureType === 'Draw' ? 'default' : 'outline'}
                                onClick={() => setSignatureType('Draw')}
                                className="flex-1 rounded-xl"
                            >
                                Draw
                            </Button>
                        </div>

                        {signatureType === 'Type' ? (
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700">Enter full name:</label>
                                <Input
                                    value={typedSignature}
                                    onChange={(e) => setTypedSignature(e.target.value)}
                                    placeholder="Your Full Name"
                                    className="font-signature text-lg rounded-xl h-12"
                                />
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700">Draw signature:</label>
                                <canvas
                                    ref={canvasRef}
                                    width={400}
                                    height={150}
                                    className="w-full border rounded-2xl cursor-crosshair bg-gray-50"
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={stopDrawing}
                                />
                                <Button type="button" variant="outline" size="sm" onClick={clearCanvas} className="rounded-lg">
                                    Clear
                                </Button>
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="outline" onClick={() => setIsSignatureDialogOpen(false)} className="rounded-xl">Cancel</Button>
                            <Button type="button" onClick={handleConfirmSignature} className="bg-black text-white rounded-xl">Confirm Signature</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AssignAssets;
